import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Divider, Collapse, Tag } from 'antd';
import {
  QuestionCircleOutlined,
  CloseOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  FieldTimeOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import './RulesHelpModal.css';

const { Panel } = Collapse;

const RulesHelpModal = ({ isDarkMode, isVisible, onClose }) => {
  const [internalVisible, setInternalVisible] = useState(false);

  // Use external visibility control if provided, otherwise use internal state
  const modalVisible = isVisible !== undefined ? isVisible : internalVisible;

  const handleClose = useCallback((visible) => {
    if (onClose) {
      onClose(visible);
    } else {
      setInternalVisible(visible);
    }
  }, [onClose]);

  // Close with Esc
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && modalVisible) {
        handleClose(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalVisible, handleClose]);

  const ruleCategories = [
    {
      key: 'risk',
      title: 'Risk Management',
      description: 'Protect your capital by enforcing position sizing and risk limits',
      icon: <SafetyOutlined />,
      rules: [
        {
          name: 'Max Risk Per Trade',
          type: 'HARDCODED',
          description: 'Limits the maximum percentage of your account equity you can risk on a single trade.',
          config: 'maxRiskPercent',
          defaultValue: '0.5%',
          example: 'If set to 0.5% with $10,000 account, max risk per trade is $50.',
          passCriteria: 'Trade risk ≤ max risk percentage',
          failCriteria: 'Trade risk > max risk percentage'
        },
        {
          name: 'Minimum R:R',
          type: 'HARDCODED',
          description: 'Ensures every trade has adequate reward-to-risk ratio before entry.',
          config: 'minRR',
          defaultValue: '1.5:1',
          example: 'Risk 50 pips to target = need at least 75 pips reward.',
          passCriteria: 'Reward ÷ Risk ≥ minimum R:R',
          failCriteria: 'Reward ÷ Risk < minimum R:R'
        }
      ]
    },
    {
      key: 'timing',
      title: 'Timing & Events',
      description: 'Trade during optimal market conditions and avoid high-impact events',
      icon: <FieldTimeOutlined />,
      rules: [
        {
          name: 'Event Proximity',
          type: 'HARDCODED',
          description: 'Blocks trades near high-impact economic events (NFP, FOMC, CPI, etc.). Helps avoid volatility spikes and slippage.',
          config: 'proximityMinutes, impactLevels',
          defaultValue: '60 minutes, HIGH impact only',
          example: 'If NFP is in 30 minutes, blocks new trades until after the event.',
          passCriteria: 'No high-impact events within proximity window',
          failCriteria: 'High-impact event detected within proximity window'
        },
        {
          name: 'Liquidity Window',
          type: 'HARDCODED',
          description: 'Ensures trading only during high-liquidity sessions. Blocks weekend and off-hours trading.',
          config: 'allowedSessions, blockWeekends',
          defaultValue: 'London + New York sessions',
          example: 'Blocks trades on Saturday, allows during London-NY overlap.',
          passCriteria: 'Current time is within allowed trading sessions',
          failCriteria: 'Market closed or in low-liquidity period'
        }
      ]
    },
    {
      key: 'technical',
      title: 'Technical Analysis',
      description: 'Validate trade setups against market structure and indicators',
      icon: <ThunderboltOutlined />,
      rules: [
        {
          name: 'Volatility Check',
          type: 'HARDCODED',
          description: 'Validates market volatility is healthy. Blocks if market is too quiet (no movement) or too volatile (high risk). Formula: Volatility% = (ATR ÷ Price) × 100',
          config: 'minVolatilityPercent, maxVolatilityPercent, timeframe',
          defaultValue: '0.1% - 5.0% volatility on H1',
          example: 'If ATR=0.0050 and price=1.1000, volatility = 0.45%. Blocks if outside your min/max range.',
          passCriteria: 'Volatility within min/max thresholds',
          failCriteria: 'Market too quiet or too volatile'
        },
        {
          name: 'Trend Filter',
          type: 'HARDCODED',
          description: 'Blocks counter-trend trades. Ensures trade direction aligns with higher timeframe trend using moving averages.',
          config: 'timeframe (M30, H1, H4, D, W), period (20, 50, 200)',
          defaultValue: 'Daily SMA 200',
          example: 'Long trades require price above SMA200, shorts require price below.',
          passCriteria: 'Trade direction matches trend (price vs SMA)',
          failCriteria: 'Trade is counter-trend'
        },
        {
          name: 'Mean Reversion Filter',
          type: 'HARDCODED',
          description: 'Prevents chasing extended moves. Blocks buying tops or selling bottoms when price deviates too far from the mean. Formula: Extended if |Price - MA| > ATR × Multiple',
          config: 'maPeriod (20, 50, 200), atrMultiple, timeframe',
          defaultValue: 'SMA 20, 3x ATR threshold',
          example: 'If price is 4 ATRs above SMA20, blocks long entries (chasing the top).',
          passCriteria: 'Price within normal range of moving average',
          failCriteria: 'Price over-extended (buying top / selling bottom)'
        }
      ]
    },
    {
      key: 'ai',
      title: 'AI-Powered Rules',
      description: 'Advanced validation using AI to analyze market structure and context',
      icon: <RobotOutlined />,
      rules: [
        {
          name: 'Key Level Proximity',
          type: 'LLM_AGENT',
          description: 'AI identifies support/resistance levels and validates if entry is at a favorable location. Adapts analysis based on your trading style (intraday or swing).',
          config: 'Trading style, custom prompt',
          defaultValue: 'Auto-fetches: market data, key levels, recent price action',
          example: 'For a long entry, AI checks if you\'re buying into nearby resistance or at a clean support zone.',
          passCriteria: 'Entry at favorable level with room to target',
          failCriteria: 'Entry too close to opposing key level'
        },
        {
          name: 'Stop Loss Placement',
          type: 'LLM_AGENT',
          description: 'AI validates that stop loss is placed beyond significant market structure. Considers swing points and liquidity zones appropriate for your timeframe.',
          config: 'Trading style, custom prompt',
          defaultValue: 'Auto-fetches: market data, swing points, ATR for context',
          example: 'For longs, AI verifies SL is below the last swing low, not inside the structure.',
          passCriteria: 'Stop placed beyond structure / swing point',
          failCriteria: 'Stop too tight or inside market structure'
        }
      ]
    }
  ];

  return (
    <Modal
      open={modalVisible}
      onCancel={() => handleClose(false)}
      footer={null}
      closable={true}
      className={`rules-help-modal ${isDarkMode ? 'dark' : 'light'}`}
      width={800}
      closeIcon={<CloseOutlined />}
    >
      <div className={`rules-help-content ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="rules-help-header">
          <QuestionCircleOutlined className="header-icon" />
          <h2>Validation Rules Guide</h2>
          <p className="subtitle">Understand how each validation rule protects your trades</p>
        </div>

        <Divider />

        <div className="rules-help-intro">
          <p>
            Validation rules run before you enter a trade to check if it meets your criteria.
            Rules can <Tag color="green">PASS</Tag>, <Tag color="red">FAIL</Tag>,
            or <Tag color="default">SKIP</Tag> (if data unavailable).
          </p>
        </div>

        <Collapse
          className="rules-help-collapse"
          accordion
          expandIconPosition="end"
        >
          {ruleCategories.map((category) => (
            <Panel
              header={
                <div className="category-header">
                  {category.icon}
                  <div className="category-info">
                    <span className="category-title">{category.title}</span>
                    <span className="category-description">{category.description}</span>
                  </div>
                </div>
              }
              key={category.key}
            >
              {category.rules.map((rule, index) => (
                <div key={index} className="rule-detail">
                  <div className="rule-title">
                    <span className="rule-name">{rule.name}</span>
                    <Tag className={`rule-type ${rule.type === 'LLM_AGENT' ? 'ai' : 'static'}`}>
                      {rule.type === 'LLM_AGENT' ? 'AI Rule' : 'Static Rule'}
                    </Tag>
                  </div>
                  <p className="rule-description">{rule.description}</p>

                  <div className="rule-config">
                    <div className="config-row">
                      <span className="config-label">Configuration:</span>
                      <code>{rule.config}</code>
                    </div>
                    <div className="config-row">
                      <span className="config-label">Default:</span>
                      <span>{rule.defaultValue}</span>
                    </div>
                    <div className="config-row">
                      <span className="config-label">Example:</span>
                      <span className="example">{rule.example}</span>
                    </div>
                  </div>

                  <div className="rule-outcomes">
                    <div className="outcome pass">
                      <CheckCircleOutlined />
                      <span><strong>Pass:</strong> {rule.passCriteria}</span>
                    </div>
                    <div className="outcome fail">
                      <CloseCircleOutlined />
                      <span><strong>Fail:</strong> {rule.failCriteria}</span>
                    </div>
                  </div>

                  {index < category.rules.length - 1 && <Divider />}
                </div>
              ))}
            </Panel>
          ))}
        </Collapse>

        <Divider />

        <div className="rules-help-footer">
          <div className="footer-tip">
            <QuestionCircleOutlined />
            <span>
              <strong>Tip:</strong> Enable only the rules that match your trading strategy.
              You can configure thresholds for each rule in the settings.
            </span>
          </div>
          <div className="close-hint">
            Press <kbd>Esc</kbd> or click outside to close
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RulesHelpModal;
